/**
 * ShipmentFeesSection — iter 264d.
 *
 * Chargeable-fee checklist for a shipment. Renders below the comments in the
 * dashboard container detail dialog AND inside the Edit Load sheet.
 * Dispatchers tick fees + fill an optional amount + note; Accounting picks
 * up the flagged fees from GET /api/accounting/pending-fees.
 *
 * The catalog is company-scoped and lazy-seeded on first read, so this
 * component makes ONE call to /api/fee-types and ONE call to
 * /api/shipments/{id}/fees on mount, then a single PUT on save.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { DollarSign, Receipt, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';


export const ShipmentFeesSection = ({ shipmentId, compact = false }) => {
  const { api } = useAuth();
  const [catalog, setCatalog] = useState([]);      // [{id, code, name, description, default_amount}]
  const [selected, setSelected] = useState({});    // { [fee_type_id]: { amount, note } }
  const [initialSelected, setInitialSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch catalog + existing shipment fees in parallel on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, feeRes] = await Promise.all([
          api.get('/fee-types'),
          api.get(`/shipments/${shipmentId}/fees`),
        ]);
        if (cancelled) return;
        setCatalog(catRes.data || []);
        const map = {};
        for (const f of (feeRes.data?.fees || [])) {
          map[f.fee_type_id] = { amount: f.amount ?? '', note: f.note ?? '' };
        }
        setSelected(map);
        setInitialSelected(JSON.parse(JSON.stringify(map))); // deep clone for dirty detection
      } catch (e) {
        console.error('[Fees] load failed:', e);
        toast.error('Failed to load fees');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shipmentId, api]);

  const isDirty = useMemo(
    () => JSON.stringify(selected) !== JSON.stringify(initialSelected),
    [selected, initialSelected]
  );

  const total = useMemo(() => {
    let sum = 0;
    Object.values(selected).forEach((v) => {
      const n = parseFloat(v?.amount);
      if (!Number.isNaN(n)) sum += n;
    });
    return sum;
  }, [selected]);

  const toggleFee = useCallback((fee) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[fee.id]) {
        delete next[fee.id];
      } else {
        next[fee.id] = {
          amount: fee.default_amount != null ? String(fee.default_amount) : '',
          note: '',
        };
      }
      return next;
    });
  }, []);

  const updateAmount = useCallback((feeId, value) => {
    setSelected((prev) => ({ ...prev, [feeId]: { ...(prev[feeId] || {}), amount: value } }));
  }, []);

  const updateNote = useCallback((feeId, value) => {
    setSelected((prev) => ({ ...prev, [feeId]: { ...(prev[feeId] || {}), note: value } }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const fees = Object.entries(selected).map(([fee_type_id, v]) => {
        const amtNum = parseFloat(v?.amount);
        return {
          fee_type_id,
          checked: true,
          amount: Number.isNaN(amtNum) ? null : amtNum,
          note: v?.note || null,
        };
      });
      await api.put(`/shipments/${shipmentId}/fees`, { fees });
      setInitialSelected(JSON.parse(JSON.stringify(selected)));
      toast.success('Billable fees saved');
    } catch (e) {
      console.error('[Fees] save failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to save fees');
    } finally {
      setSaving(false);
    }
  }, [selected, shipmentId, api]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading fees...
      </div>
    );
  }

  const selectedCount = Object.keys(selected).length;

  return (
    <Card
      className={`border-slate-800 bg-slate-900/40 ${compact ? '' : 'shadow-sm'}`}
      data-testid="shipment-fees-section"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-200">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-emerald-400" />
            <span>Billable Fees</span>
            {selectedCount > 0 && (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-xs px-2">
                {selectedCount} flagged
              </Badge>
            )}
          </div>
          {total > 0 && (
            <div className="flex items-center gap-1 text-emerald-400 text-sm font-mono">
              <DollarSign className="h-3.5 w-3.5" />
              {total.toFixed(2)}
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
          {catalog.map((fee) => {
            const isChecked = !!selected[fee.id];
            return (
              <div
                key={fee.id}
                className={`flex items-start gap-2 py-1.5 px-2 rounded border ${
                  isChecked
                    ? 'bg-emerald-500/5 border-emerald-500/30'
                    : 'border-transparent hover:border-slate-700 hover:bg-slate-800/40'
                }`}
                data-testid={`fee-row-${fee.code}`}
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggleFee(fee)}
                  className="mt-0.5"
                  data-testid={`fee-checkbox-${fee.code}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={`text-sm font-medium truncate ${
                        isChecked ? 'text-slate-100' : 'text-slate-300'
                      }`}
                      onClick={() => toggleFee(fee)}
                      style={{ cursor: 'pointer' }}
                    >
                      {fee.name}
                    </span>
                    {fee.code && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-500 font-mono flex-shrink-0">
                        {fee.code}
                      </span>
                    )}
                  </div>
                  {isChecked && (
                    <div className="mt-1 grid grid-cols-3 gap-1.5">
                      <div className="col-span-1 relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={selected[fee.id]?.amount ?? ''}
                          onChange={(e) => updateAmount(fee.id, e.target.value)}
                          className="pl-5 h-7 text-xs bg-slate-900 border-slate-700 text-slate-100"
                          data-testid={`fee-amount-${fee.code}`}
                        />
                      </div>
                      <Input
                        type="text"
                        placeholder="Note (optional)"
                        value={selected[fee.id]?.note ?? ''}
                        onChange={(e) => updateNote(fee.id, e.target.value)}
                        className="col-span-2 h-7 text-xs bg-slate-900 border-slate-700 text-slate-100"
                        data-testid={`fee-note-${fee.code}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pt-2 border-t border-slate-800 flex items-center justify-end gap-2">
          {isDirty && (
            <span className="text-xs text-amber-400">Unsaved changes</span>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!isDirty || saving}
            className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
            data-testid="fees-save-btn"
          >
            {saving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...</>
            ) : (
              <><Save className="h-3.5 w-3.5 mr-1.5" /> Save fees</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShipmentFeesSection;
